import { useState, useEffect } from "react";
import { useAtomValue } from "jotai";
import { Check } from "lucide-react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "@design-lab/shared/ui";
import { transferTypesAtom } from "../model/transfer-types.machine";

type TransferTypeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (types: string[]) => void;
  selectedTypes: string[];
};

export function TransferTypeModal({
  isOpen,
  onClose,
  onSelect,
  selectedTypes
}: TransferTypeModalProps) {
  const transferTypesState = useAtomValue(transferTypesAtom);
  const { transferTypes, loading, error } = transferTypesState.context;

  // Local state for immediate UI updates
  const [localSelectedTypes, setLocalSelectedTypes] = useState<string[]>(selectedTypes);

  // Sync local state with props when modal opens
  useEffect(() => {
    if (isOpen) {
      setLocalSelectedTypes(selectedTypes);
    }
  }, [isOpen, selectedTypes]);

  const handleToggleType = (typeId: string) => {
    setLocalSelectedTypes(prev => {
      if (prev.includes(typeId)) {
        return prev.filter(id => id !== typeId);
      }
      return [...prev, typeId];
    });
  };

  const handleConfirm = () => {
    onSelect(localSelectedTypes);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="flex h-[90vh] max-h-[600px] w-full max-w-4xl flex-col md:h-[80vh]"
      title="Select Transfer Type"
      description="Choose one or more transfer types"
    >
      <ModalHeader onClose={onClose}>
        <div>
          <h2 className="text-xl font-semibold">Select Transfer Type</h2>
          <p className="mt-1 text-sm text-gray-500">Choose one or more transfer types</p>
        </div>
      </ModalHeader>

      <ModalBody className="flex-1 overflow-y-auto p-6">
        {loading && <div className="flex items-center justify-center p-8">Loading...</div>}
        {error && (
          <div className="flex items-center justify-center p-8 text-red-600">
            Error loading transfer types
          </div>
        )}
        {!loading && !error && (
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
            {transferTypes.map(type => {
              const isSelected = localSelectedTypes.includes(type.id);
              return (
                <button
                  type="button"
                  key={type.id}
                  onClick={() => handleToggleType(type.id)}
                  className={`relative flex flex-col items-center rounded-lg border-2 p-4 transition-all duration-200 hover:shadow-md ${
                    isSelected
                      ? "border-blue-500 bg-blue-50"
                      : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                  data-testid={`transfer-type-${type.id}`}
                >
                  {/* Checkmark indicator */}
                  {isSelected && (
                    <div className="absolute right-2 top-2">
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-blue-500">
                        <Check className="h-3 w-3 text-white" strokeWidth={3} />
                      </div>
                    </div>
                  )}

                  {/* Image or Placeholder */}
                  <div className="mb-3 flex h-28 w-28 items-center justify-center overflow-hidden rounded-lg bg-gray-50">
                    {type.image ? (
                      <img src={type.image} alt={type.name} className="h-28 w-28 object-contain" />
                    ) : (
                      <div className="flex h-28 w-28 items-center justify-center text-gray-400">
                        <span className="text-sm font-medium">{type.name.charAt(0)}</span>
                      </div>
                    )}
                  </div>

                  {/* Type Name */}
                  <span className="text-center text-sm font-medium text-gray-900">{type.name}</span>
                </button>
              );
            })}
          </div>
        )}
      </ModalBody>

      <ModalFooter className="flex shrink-0 border-0">
        <button
          type="button"
          onClick={handleConfirm}
          className="ml-auto rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          data-testid="confirm-button"
        >
          Confirm
        </button>
      </ModalFooter>
    </Modal>
  );
}
